import React, { useEffect, useState, useRef } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  withStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import { appService } from '../../App/app.service';
import AddHeaderDescriptionWithHelperText from '../../components/HelperTextDescription';
import RSMPerformanceTable from './RSMPerformanceTable';
import SingleLevelDrilldown from '../../components/Drilldown/SingleLevelDrilldown';
import DrilldownWithSLD from '../../components/Drilldown/DrilldownWithSLD';
import ExpectedAcvPage from '../NewWaterFall/ExpectedAcvPage';
import RegionalRSMPerformancePipelineOverview from './RegionalRSMPerformancePipelineOverview';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#333',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  filterWrapper: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  filterContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'inline-flex',
    width: 'auto',
  },
  formControl: {
    minWidth: 200,
  },
  goButton: {
    borderRadius: 4,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
  },
  filterLabel: {
    color: '#000',
  },
});

const RegionalRSMPerformanceSummary = (props) => {
  const { classes, rsmFirstLevelTeams, rsmDefaultTeam, rsmRepList, rsmPerformanceData, rsmRepChartsData, helperTextCharts } = props;

  // Local state for pending team selection (before clicking Go)
  const [pendingTeam, setPendingTeam] = useState('All');
  // Applied team filter (after clicking Go)
  const [appliedTeam, setAppliedTeam] = useState('All');
  // Local state for pending rep selection (before clicking Go)
  const [pendingRep, setPendingRep] = useState('All');
  // Applied rep filter (after clicking Go)
  const [appliedRep, setAppliedRep] = useState('All');
  // Track if initial data has been loaded
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Drilldown state
  const [secondLevelLocation, setSecondLevelLocation] = useState('');
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownType, setDrilldownType] = useState(''); // 'qualifiedPipeline' or 'wonYTD'
  const [drilldownFilters, setDrilldownFilters] = useState([]);
  const defaultRepChartsFetchedRef = useRef(false);

  // Client-side filtered rep list based on selected team
  const filteredRepList = React.useMemo(() => {
    if (!rsmRepList || rsmRepList.length === 0) return [];
    if (pendingTeam === 'All') return rsmRepList;
    return rsmRepList.filter(rep => rep.team === pendingTeam);
  }, [rsmRepList, pendingTeam]);

  // Reset when component mounts
  useEffect(() => {
    setPendingTeam('All');
    setAppliedTeam('All');
    setPendingRep('All');
    setAppliedRep('All');
    setInitialLoadDone(false);
    defaultRepChartsFetchedRef.current = false;
  }, []);

  // Fetch first-level teams on mount (once)
  useEffect(() => {
    props.getFirstLevelTeams();
  }, []);

  // Fetch rep list on mount (once, like first-level-teams)
  useEffect(() => {
    props.getRepList([]);
  }, []);

  // Set default team when teams are loaded (uses manager's team if applicable)
  useEffect(() => {
    if (!initialLoadDone && rsmFirstLevelTeams && rsmFirstLevelTeams.length > 0) {
      const defaultTeam = rsmDefaultTeam || 'All';
      setPendingTeam(defaultTeam);
      setAppliedTeam(defaultTeam);
      setPendingRep('All');
      setAppliedRep('All');
      const filters = defaultTeam !== 'All' ? [{ name: 'team', value: defaultTeam }] : [];
      props.getRSMPerformanceData(filters);
      setInitialLoadDone(true);
    }
  }, [rsmFirstLevelTeams, rsmDefaultTeam, initialLoadDone]);

  // Do not fetch rep charts by default; only after explicit rep selection via GO

  // Handle team filter change (just updates pending, doesn't fetch); reset rep when team changes
  const handleTeamChange = (event) => {
    const teamValue = event.target.value;
    setPendingTeam(teamValue);
    setPendingRep('All');
    // No API call needed - filtering happens in-memory via filteredRepList
  };

  // Handle rep filter change (just updates pending, doesn't fetch); reset team when rep selected (mutual exclusivity)
  const handleRepChange = (event) => {
    const value = event.target.value;
    setPendingRep(value);
  };

  // Handle Go button click - apply the filters and fetch data
  const handleGo = () => {
    setAppliedTeam(pendingTeam);
    setAppliedRep(pendingRep);
    const filters = [];
    if (pendingTeam !== 'All') filters.push({ name: 'team', value: pendingTeam });
    if (pendingRep !== 'All') filters.push({ name: 'repName', value: pendingRep });
    props.getRSMPerformanceData(filters);
    // Only fetch rep charts if a specific rep is selected
    const repForCharts = filters.find((f) => f.name === 'repName')?.value;
    if (repForCharts) props.getRSMRepChartsData([{ name: 'repName', value: repForCharts }]);
  };

  // Handler for RSM Performance table cell click drilldown
  const handleDrilldown = ({ type, repName }) => {
    const filters = [
      { name: 'repName', value: repName },
    ];
    // Include team and rep filters if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }
    if (appliedRep && appliedRep !== 'All') {
      filters.push({ name: 'repName', value: appliedRep });
    }

    setDrilldownOpen(true);
    setDrilldownType(type);

    // Build display filters
    const displayFilters = [
      { title: 'RSM', value: repName },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    if (appliedRep && appliedRep !== 'All') {
      displayFilters.push({ title: 'Rep', value: appliedRep });
    }
    setDrilldownFilters(displayFilters);

    if (type === 'qualifiedPipeline') {
      props.getRSMQualifiedPipelineDrilldown(filters);
    } else if (type === 'wonYTD') {
      props.getRSMWonYTDDrilldown(filters);
    }
  };

  // Handler to close drilldown
  const handleDrilldownBack = () => {
    setDrilldownOpen(false);
    setDrilldownType('');
    setDrilldownFilters([]);
    setSecondLevelLocation('');
    props.clearRSMDrilldown();
    props.clearExpectedAcvData();
  };

  // Handler for second level drilldown (opportunity change events)
  const handleSecondLevelDrilldown = (location, oppId) => {
    setSecondLevelLocation(location);
    props.getOpenOppData(oppId);
  };

  // Step back from second level to first level drilldown
  const handleStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  const isLoading = !rsmPerformanceData || rsmPerformanceData === "";

  return (
    <div className={classes.root}>
      <AddHeaderDescriptionWithHelperText
        header={`Regional RSM Performance Summary`}
        description={`Regional RSM Performance Summary.`}
        helperTextCharts={helperTextCharts}
      />

      {/* Team and Rep Filters */}
      <Box className={classes.filterWrapper}>
        <Paper className={classes.filterContainer} elevation={0}>
          <FormControl className={classes.formControl}>
            <InputLabel
              className={classes.filterLabel}
              id="rsm-team-filter-label"
              style={{
                color: pendingTeam !== '' && pendingTeam !== 'All' ? '#4472c4' : '#000',
              }}
            >
              Team:
            </InputLabel>
            <Select
              labelId="rsm-team-filter-label"
              id="rsm-team-filter"
              value={pendingTeam}
              onChange={handleTeamChange}
              label="Team:"
             
            >
              <MenuItem value="All">
                All
              </MenuItem>
              {rsmFirstLevelTeams && rsmFirstLevelTeams.map((team) => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl} style={{ marginLeft: 16 }}>
            <InputLabel
              className={classes.filterLabel}
              id="rsm-rep-filter-label"
              style={{
                color: pendingRep !== '' && pendingRep !== 'All' ? '#4472c4' : '#000',
              }}
            >
              Rep:
            </InputLabel>
            <Select
              labelId="rsm-rep-filter-label"
              id="rsm-rep-filter"
              value={pendingRep}
              onChange={handleRepChange}
              label="Rep:"
              
            >
              <MenuItem value="All">
                All
              </MenuItem>
              {filteredRepList && filteredRepList.map((rep) => (
                <MenuItem key={rep.name} value={rep.name}>
                  {rep.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
        <Button
          color="primary"
          variant="contained"
          onClick={handleGo}
          className={classes.goButton}
        >
          GO
        </Button>
      </Box>

      {/* RSM Performance Table */}
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {rsmPerformanceData?.title || 'RSM Performance Summary'}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>
        </div>

        {isLoading ? (
          <Skeleton variant="rect" animation="wave" height={400} width="100%" />
        ) : rsmPerformanceData?.tableData?.length > 0 ? (
          <RSMPerformanceTable
            data={rsmPerformanceData.tableData}
            fiscalYear={rsmPerformanceData.fiscalYear}
            quarters={rsmPerformanceData.quarters}
            stages={rsmPerformanceData.stages}
            onDrill={handleDrilldown}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        )}
      </Paper>

      {appliedRep !== 'All' && (
        <RegionalRSMPerformancePipelineOverview data={rsmRepChartsData} />
      )}

      {/* RSM Performance Drilldown - First Level */}
      {drilldownOpen && secondLevelLocation !== 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={drilldownType === 'qualifiedPipeline'
              ? 'Current Qualified Pipeline Opportunities'
              : 'Won YTD Opportunities'}
            back={handleDrilldownBack}
            filters={drilldownFilters}
          >
            <DrilldownWithSLD
              header={drilldownType === 'qualifiedPipeline'
                ? 'Open Qualified Pipeline'
                : 'Closed-Won Opportunities YTD'}
              body={props.rsmDrilldownData}
              company={props.company}
              Value_Label={props.Value_Label}
              handleSecondLevelDrilldown={handleSecondLevelDrilldown}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

      {/* RSM Performance Drilldown - Second Level (Opportunity Change Events) */}
      {drilldownOpen && secondLevelLocation === 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={"Opportunity Change Events"}
            back={handleDrilldownBack}
            stepBack={handleStepBack}
            filters={[]}
          >
            <ExpectedAcvPage
              body={props.ExpectedAcvData}
              Value_Label={props.Value_Label}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, rsmFirstLevelTeams, rsmDefaultTeam, rsmRepList, rsmPerformanceData, rsmDrilldownData, rsmRepChartsData, ExpectedAcvData } = state.app;

  return {
    company: user.company,
    Value_Label: user.Value_Label,
    rsmFirstLevelTeams: rsmFirstLevelTeams || [],
    rsmDefaultTeam: rsmDefaultTeam || 'All',
    rsmRepList: rsmRepList || [],
    rsmPerformanceData: rsmPerformanceData || null,
    rsmDrilldownData: rsmDrilldownData,
    rsmRepChartsData: rsmRepChartsData,
    ExpectedAcvData: ExpectedAcvData,
    helperTextCharts: user.elements.flatMap((item) => item.charts || []),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getFirstLevelTeams: () => {
    dispatch({ type: "get_rsm_first_level_teams_request" });
    appService.getPipelineReviewFirstLevelTeams().then(
      (json) => {
        dispatch({ type: "get_rsm_first_level_teams_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_first_level_teams_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_first_level_teams_failure", error });
      }
    );
  },
  getRepList: (filters = []) => {
    dispatch({ type: "get_rsm_rep_list_request" });
    appService.getRSMRepList(filters).then(
      (json) => {
        dispatch({ type: "get_rsm_rep_list_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_rep_list_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_rep_list_failure", error });
      }
    );
  },
  getRSMPerformanceData: (filters = []) => {
    dispatch({ type: "get_rsm_performance_data_request" });
    appService.getRSMPerformanceSummary(filters).then(
      (json) => {
        dispatch({ type: "get_rsm_performance_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_performance_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_performance_data_failure", error });
      }
    );
  },
  getRSMQualifiedPipelineDrilldown: (filters = []) => {
    dispatch({ type: "get_rsm_drilldown_request" });
    appService.getRSMQualifiedPipelineDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_rsm_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_drilldown_failure", error });
      }
    );
  },
  getRSMWonYTDDrilldown: (filters = []) => {
    dispatch({ type: "get_rsm_drilldown_request" });
    appService.getRSMWonYTDDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_rsm_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_drilldown_failure", error });
      }
    );
  },
  getRSMRepChartsData: (filters = []) => {
    dispatch({ type: "get_rsm_rep_charts_request" });
    appService.getRSMRepCharts(filters).then(
      (json) => {
        dispatch({ type: "get_rsm_rep_charts_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_rep_charts_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_rep_charts_failure", error });
      }
    );
  },
  clearRSMDrilldown: () => {
    dispatch({ type: "clear_rsm_drilldown" });
  },
  getOpenOppData: (oppId) => {
    dispatch({ type: "get_expectedAcv_drilldown_data_request" });
    appService.getWaterfallOppData(oppId).then(
      (json) => {
        dispatch({ type: "get_expectedAcv_drilldown_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_expectedAcv_drilldown_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_expectedAcv_drilldown_data_failure", error });
      }
    );
  },
  clearExpectedAcvData: () => {
    dispatch({ type: "clear_expected_acv_data" });
  },
});

const connectedRegionalRSMPerformanceSummary = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(RegionalRSMPerformanceSummary));

export default connectedRegionalRSMPerformanceSummary;
